/* useAiJob — shared polling helper for background admin batch generations.
 *
 * Long-running admin AI batches (Kitchen Help, Historical) exceed Cloudflare's
 * 120s Proxy Read Timeout, so the backend returns `{ job_id, status: "running" }`
 * immediately and streams progress into an `ai_jobs` collection. This hook
 * polls `statusUrl(job_id)` every 3s and exposes `{ progress, done, error, cancel }`.
 *
 * Usage:
 *   const job = useAiJob({ statusUrl: (id) => `/admin/kitchen-help/generate/status/${id}` });
 *   await job.start(async () => api.post('/admin/kitchen-help/generate', { count: 50 }));
 *   // → job.progress → { inserted, skipped, processed, total, status, requested }
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

export default function useAiJob({ statusUrl, pollMs = 3000, onDone, onError, onCancelled } = {}) {
  const [progress, setProgress] = useState(null);   // { inserted, skipped, processed, total, status, requested, result }
  const [busy, setBusy] = useState(false);
  const timerRef = useRef(null);
  const jobIdRef = useRef(null);

  const stop = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => stop, [stop]);

  /**
   * Start a job. `postFn` must fire the POST and return the response `data` payload.
   * If the payload is a fully-populated sync result (no `job_id`), we resolve
   * immediately and call `onDone`. Otherwise we spin up a poll loop.
   */
  const start = useCallback(async (postFn) => {
    setBusy(true);
    setProgress(null);
    jobIdRef.current = null;
    try {
      const data = await postFn();
      // ---- Sync path: server returned the final result directly ----
      if (!data?.job_id) {
        setBusy(false);
        onDone?.(data);
        return data;
      }
      // ---- Async path: poll the status endpoint until done/error ----
      jobIdRef.current = data.job_id;
      setProgress({
        job_id: data.job_id,
        status: "running",
        inserted: 0,
        skipped: 0,
        processed: 0,
        total: data.total || 0,
        requested: data.requested || 0,
      });
      const url = statusUrl(data.job_id);
      const tick = async () => {
        try {
          const { data: job } = await api.get(url);
          setProgress(job);
          if (job.status === "done") {
            stop();
            setBusy(false);
            onDone?.(job);
          } else if (job.status === "cancelled") {
            stop();
            setBusy(false);
            onCancelled?.(job);
          } else if (job.status === "error") {
            stop();
            setBusy(false);
            onError?.(new Error(job.error || "AI job failed"));
          }
        } catch (e) {
          stop();
          setBusy(false);
          onError?.(e);
        }
      };
      // Fire once immediately for a snappy first update, then interval.
      tick();
      timerRef.current = setInterval(tick, pollMs);
      return data;
    } catch (e) {
      setBusy(false);
      onError?.(e);
      throw e;
    }
  }, [statusUrl, pollMs, onDone, onError, onCancelled, stop]);

  /**
   * Ask the backend to cancel the currently running job. Worker will honour
   * the flag at the next safe stopping point; keep polling so the UI can flip
   * to the final "cancelled" state naturally.
   */
  const cancel = useCallback(async () => {
    const jid = jobIdRef.current;
    if (!jid) return;
    try {
      await api.post(`/admin/ai-jobs/${jid}/cancel`);
    } catch (e) {
      // If cancel POST fails (job already finished, network hiccup), we just
      // let the next poll tick reveal the terminal state — no toast noise.
    }
  }, []);

  return { start, progress, busy, cancel, stop };
}
